import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useSettings, useUpdateSettings, type SystemSettings } from "@/hooks/use-settings";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Settings, Save } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { config } from "@/lib/config/brand.config";

export const Route = createFileRoute("/settings/")({
  component: SettingsPage,
});
function SettingsPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <SettingsPageContent />
      </AppShell>
    </ProtectedRoute>
  );
}


function SettingsPageContent() {
  const { data: settings, isLoading } = useSettings();
  const updateMutation = useUpdateSettings();

  const [formData, setFormData] = useState<Partial<SystemSettings>>({});

  // Update form data when settings load
  useState(() => {
    if (settings && Object.keys(formData).length === 0) {
      setFormData(settings);
    }
  });

  const handleUpdate = async () => {
    try {
      await updateMutation.mutateAsync(formData);
      toast.success("Settings updated successfully");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Settings className="w-8 h-8" />
            System Settings
          </h1>
          <p className="text-muted-foreground">
            Configure system-wide settings and parameters
          </p>
        </div>
        <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
          <Save className="w-4 h-4 mr-2" />
          {updateMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle>General Settings</CardTitle>
          <CardDescription>
            Basic system information and contact details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="system_name">System Name</Label>
              <Input
                id="system_name"
                value={formData.system_name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, system_name: e.target.value })
                }
                placeholder={config().companyName}
              />
            </div>
            <div>
              <Label htmlFor="system_email">System Email</Label>
              <Input
                id="system_email"
                type="email"
                value={formData.system_email || ""}
                onChange={(e) =>
                  setFormData({ ...formData, system_email: e.target.value })
                }
                placeholder="system@dhamaka.com"
              />
            </div>
            <div>
              <Label htmlFor="support_email">Support Email</Label>
              <Input
                id="support_email"
                type="email"
                value={formData.support_email || ""}
                onChange={(e) =>
                  setFormData({ ...formData, support_email: e.target.value })
                }
                placeholder="support@dhamaka.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commission Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Commission Settings</CardTitle>
          <CardDescription>
            Default commission rates for merchants and agents
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="merchant_commission">
                Merchant Commission Rate (%)
              </Label>
              <Input
                id="merchant_commission"
                type="number"
                step="0.1"
                value={formData.default_merchant_commission_rate || 2.5}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_merchant_commission_rate: Number.parseFloat(e.target.value),
                  })
                }
              />
              <p className="text-sm text-muted-foreground mt-1">
                Default: 2.5%
              </p>
            </div>
            <div>
              <Label htmlFor="agent_commission">Agent Commission Rate (%)</Label>
              <Input
                id="agent_commission"
                type="number"
                step="0.1"
                value={formData.default_agent_commission_rate || 0.5}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_agent_commission_rate: Number.parseFloat(e.target.value),
                  })
                }
              />
              <p className="text-sm text-muted-foreground mt-1">
                Default: 0.5%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Order Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Order Settings</CardTitle>
          <CardDescription>
            Configure order expiry and auto-approval thresholds
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="order_expiry">Order Expiry (minutes)</Label>
              <Input
                id="order_expiry"
                type="number"
                value={formData.order_expiry_minutes || 15}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    order_expiry_minutes: Number.parseInt(e.target.value),
                  })
                }
              />
              <p className="text-sm text-muted-foreground mt-1">
                Orders expire after this duration
              </p>
            </div>
            <div>
              <Label htmlFor="auto_approve">Auto-Approve Threshold</Label>
              <Input
                id="auto_approve"
                type="number"
                value={formData.auto_approve_threshold || 18}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    auto_approve_threshold: Number.parseInt(e.target.value),
                  })
                }
              />
              <p className="text-sm text-muted-foreground mt-1">
                Minimum matching score for auto-approval
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction Limits</CardTitle>
          <CardDescription>
            Set limits for transactions and daily volumes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="max_daily">Max Daily Transactions (per SIM)</Label>
              <Input
                id="max_daily"
                type="number"
                value={formData.max_daily_transactions_per_sim || 100}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_daily_transactions_per_sim: Number.parseInt(e.target.value),
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="min_amount">Min Transaction Amount (BDT)</Label>
              <Input
                id="min_amount"
                type="number"
                value={formData.min_transaction_amount || 100}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    min_transaction_amount: Number.parseInt(e.target.value),
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="max_amount">Max Transaction Amount (BDT)</Label>
              <Input
                id="max_amount"
                type="number"
                value={formData.max_transaction_amount || 100000}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_transaction_amount: Number.parseInt(e.target.value),
                  })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fraud Detection */}
      <Card>
        <CardHeader>
          <CardTitle>Fraud Detection</CardTitle>
          <CardDescription>
            Configure fraud detection and velocity checks
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Fraud Detection</Label>
              <p className="text-sm text-muted-foreground">
                Automatically detect suspicious transactions
              </p>
            </div>
            <Switch
              checked={formData.fraud_detection_enabled !== false}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, fraud_detection_enabled: checked })
              }
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Velocity Check</Label>
              <p className="text-sm text-muted-foreground">
                Check for rapid transaction patterns
              </p>
            </div>
            <Switch
              checked={formData.velocity_check_enabled !== false}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, velocity_check_enabled: checked })
              }
            />
          </div>

          {formData.velocity_check_enabled !== false && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="velocity_max">Max Transactions</Label>
                <Input
                  id="velocity_max"
                  type="number"
                  value={formData.velocity_check_max_transactions || 10}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      velocity_check_max_transactions: Number.parseInt(e.target.value),
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="velocity_window">Time Window (minutes)</Label>
                <Input
                  id="velocity_window"
                  type="number"
                  value={formData.velocity_check_time_window_minutes || 5}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      velocity_check_time_window_minutes: Number.parseInt(e.target.value),
                    })
                  }
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Webhook Settings</CardTitle>
          <CardDescription>
            Configure webhook delivery parameters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="webhook_timeout">Timeout (seconds)</Label>
              <Input
                id="webhook_timeout"
                type="number"
                value={formData.webhook_timeout_seconds || 10}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    webhook_timeout_seconds: Number.parseInt(e.target.value),
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="webhook_retries">Max Retries</Label>
              <Input
                id="webhook_retries"
                type="number"
                value={formData.webhook_max_retries || 5}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    webhook_max_retries: Number.parseInt(e.target.value),
                  })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Maintenance Mode */}
      <Card>
        <CardHeader>
          <CardTitle>Maintenance Mode</CardTitle>
          <CardDescription>
            Enable maintenance mode to prevent new transactions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Maintenance Mode</Label>
              <p className="text-sm text-muted-foreground">
                Temporarily disable the system
              </p>
            </div>
            <Switch
              checked={formData.maintenance_mode === true}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, maintenance_mode: checked })
              }
            />
          </div>

          {formData.maintenance_mode && (
            <div>
              <Label htmlFor="maintenance_message">Maintenance Message</Label>
              <Textarea
                id="maintenance_message"
                value={formData.maintenance_message || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maintenance_message: e.target.value,
                  })
                }
                placeholder="System is under maintenance. Please try again later."
                rows={3}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button at Bottom */}
      <div className="flex justify-end">
        <Button onClick={handleUpdate} disabled={updateMutation.isPending} size="lg">
          <Save className="w-4 h-4 mr-2" />
          {updateMutation.isPending ? "Saving..." : "Save All Changes"}
        </Button>
      </div>
    </div>
  );
}
